/**
 * Capa tipada de la PROPUESTA v0 y el TABLERO del proceso (cierre del hito M0:
 * "de un problema a una propuesta trazable").
 *
 * Métodos sobre {@link apiFetch}/{@link apiDownload} para el contrato de API
 * compartido con el backend (prefijo /api/v1, Bearer resuelto en lib/api.ts).
 *
 * Contratos de datos respetados desde el consumo:
 *   C2  la genealogía de cada bloque son refs a artefacto/aporte de origen.
 *   C7  el tablero NUNCA expone métricas por persona: solo agregados por grupo
 *       y el Gini de participación como un número único.
 *
 * Índice de trazabilidad = (bloques con al menos una ref / bloques totales)*100.
 */
import { apiFetch, apiDownload } from './api';

/* ============================ Propuesta ================================= */

/** Una referencia de genealogía: liga un bloque a su aporte y artefacto origen. */
export interface PropuestaRef {
  aporte_id: string;
  artefacto_id: string;
}

/** Un bloque de texto de la propuesta con su respaldo de evidencia (C2). */
export interface PropuestaBloque {
  texto: string;
  /** true si el bloque tiene al menos una ref de genealogía. */
  respaldado: boolean;
  refs: PropuestaRef[];
  /** true si es intro/relleno de sección (no cuenta en el índice de trazabilidad). */
  estructural?: boolean;
}

/** Una de las secciones (M0 define 6) con sus bloques. */
export interface PropuestaSeccion {
  orden: number;
  titulo: string;
  bloques: PropuestaBloque[];
}

export interface PropuestaContenido {
  secciones: PropuestaSeccion[];
}

/**
 * Detalle humano de un aporte citado en la genealogía (quién/cuándo/qué).
 * `autor` null = aporte anónimo de sala (la UI muestra «Participante de sala»).
 */
export interface GenealogiaAporte {
  autor: string | null;
  grupo: string | null;
  /** Fecha ISO del aporte. */
  fecha: string;
  tipo: string;
  texto: string | null;
  herramienta: string | null;
}

/** GET /propuestas/{id}/genealogia — mapa aporte_id → detalle del aporte. */
export interface PropuestaGenealogia {
  aportes: Record<string, GenealogiaAporte>;
}

/** Propuesta v0 completa devuelta por POST/GET /procesos/{id}/propuesta. */
export interface Propuesta {
  id: string;
  proceso_id: string;
  version: number;
  /** 0–100. Verde en la UI si es ≥ 80. */
  indice_trazabilidad: number;
  contenido: PropuestaContenido;
}

/* ============================ Tablero (C7) ============================== */

export interface DashboardAsistencia {
  participantes_total: number;
  con_aportes: number;
}

export interface DashboardGrupo {
  grupo: string;
  n: number;
}

export interface DashboardAportes {
  total: number;
  por_grupo: DashboardGrupo[];
}

export interface DashboardTrazabilidad {
  indice: number;
  bloques_con_ref: number;
  bloques_total: number;
}

export interface Dashboard {
  asistencia: DashboardAsistencia;
  aportes: DashboardAportes;
  trazabilidad: DashboardTrazabilidad;
  /** 0–1. 0 = participación equitativa (todos aportan parejo). SOLO agregado. */
  gini_participacion: number;
}

/* ============================ Métodos API ============================== */

export const propuestaApi = {
  /** Última propuesta v0 del proceso; 404 humano si aún no hay artefactos. */
  obtener: (procesoId: string) =>
    apiFetch<Propuesta>(`/procesos/${procesoId}/propuesta`),

  /** Genera/regenera la propuesta v0 desde los artefactos cerrados del proceso. */
  generar: (procesoId: string) =>
    apiFetch<Propuesta>(`/procesos/${procesoId}/propuesta`, { method: 'POST' }),

  /** Tablero del proceso: agregados de asistencia, aportes, trazabilidad y Gini. */
  dashboard: (procesoId: string) =>
    apiFetch<Dashboard>(`/procesos/${procesoId}/dashboard`),

  /** Markdown de la propuesta con la genealogía como notas al pie (blob). */
  exportMd: (propuestaId: string) =>
    apiDownload(`/propuestas/${propuestaId}/export.md`),

  /** Genealogía humana de la propuesta: quién/cuándo/qué por cada aporte citado. */
  genealogia: (propuestaId: string) =>
    apiFetch<PropuestaGenealogia>(`/propuestas/${propuestaId}/genealogia`),
};

/* ============================ Query keys =============================== */

export const propuestaKeys = {
  propuesta: (procesoId: string) => ['propuesta', procesoId] as const,
  dashboard: (procesoId: string) => ['dashboard', procesoId] as const,
  genealogia: (propuestaId: string) => ['genealogia', propuestaId] as const,
};

/** Fecha corta legible (es-GT) para la genealogía; null si el ISO no parsea. */
export function fechaCortaGt(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-GT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ============================ Helpers ================================= */

/** ¿El índice de trazabilidad alcanza el umbral "verde" (≥ 80)? */
export function trazabilidadOk(indice: number): boolean {
  return indice >= 80;
}

/**
 * Descarga la propuesta como archivo .md en el navegador. Pide el blob con
 * auth, arma un enlace efímero y lo revoca. Devuelve nada; lanza ApiError si
 * el backend responde con error.
 */
export async function descargarPropuestaMd(
  propuestaId: string,
  nombreArchivo = 'propuesta.md',
): Promise<void> {
  const blob = await propuestaApi.exportMd(propuestaId);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = nombreArchivo;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Nombre de archivo estable y legible para la descarga de una propuesta. */
export function nombreArchivoPropuesta(nombreProceso: string | null | undefined): string {
  const base = (nombreProceso ?? 'propuesta')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // quita tildes
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${base || 'propuesta'}-v0.md`;
}
